import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useCallback, useState, } from 'react'
import { FEATURE_TABLE_TOOL } from '../../../../constants/map';
import { USER_DASHBOARD_HEIGHT } from '../../../../constants/insight';
import { selectActiveLayer, selectAttributeQueryParams, selectDrawGeometry, selectDrawSource, selectExtentGeometry, selectInsightControl, selectLayerList, selectMap, selectSelectedDrawGeometryTool, selectSelectedFeatureId, selectSelectedFilter, selectShowFeatureTable } from '../../../../selectors/insightSelectors';
import { LayerListItem } from '../../../../types/layer-list-item';
import { processRoleLayers, setActiveLayer, setAttributeQueryParams, setDrawGeometry, setExtentGeometry, setLayerList, setSelectedDrawGeometryTool, setSelectedFeatureId, setSelectedFilter, setShowLayerList, setShowTilebar, setShowToolbar, setToolbarItemActiveStatus } from '../../../../state/insightSlice';
import { RoleMap } from '../../../../types/layer';
import { Geometry } from 'ol/geom';
import { SingleSelectionToolbarItem } from '../../../../types/toolbar-item';
import { AttributeQueryParam } from '../../../../types/attribute-filter';
import FeatureTable from '../featureTable/FeatureTable';
import useGetLayersByRoleAndClient from 'queryHooks/layerQueryHooks/useGetLayersByRoleAndClient';
import { MapToolkit, SplitterLayout, TileWmsLayer } from '@trcsolutions/springboard-ui';
import { baseInstance } from 'axios/instances';
import config from 'config';

const tablePaneSize = 40

const Map = () => {
    const dispatch = useAppDispatch()
    const map = useAppSelector(selectMap)
    const roleId = localStorage.getItem("role_id");
    const selectedFeatureId = useAppSelector(selectSelectedFeatureId)
    useGetLayersByRoleAndClient({ roleId: roleId as string, successCb })
    const [mapHeight, setMapHeight] = useState(USER_DASHBOARD_HEIGHT - 1)
    const showFeatureTable = useAppSelector(selectShowFeatureTable)
    const layerList = useAppSelector(selectLayerList)
    const activeLayer = useAppSelector(selectActiveLayer)
    const { showLayerList, showToolbar, showTileBar, toolbarItems } = useAppSelector(selectInsightControl)
    const selectedDrawGeometryTool = useAppSelector(selectSelectedDrawGeometryTool)
    const drawSource = useAppSelector(selectDrawSource)
    const drawGeometry = useAppSelector(selectDrawGeometry)
    const attributeQueryParams = useAppSelector(selectAttributeQueryParams)
    const extentGeometry = useAppSelector(selectExtentGeometry)
    const selectedFilter = useAppSelector(selectSelectedFilter)

    function successCb(data: Array<RoleMap>) {
        if (data?.length > 0) {
            dispatch(processRoleLayers(data))
        }
    }
    const handleSecondaryPaneSize = (secondaryPaneSize: number) => {
        const mapH = ((100 - secondaryPaneSize - 1) * USER_DASHBOARD_HEIGHT) / 100
        setMapHeight(mapH)
        if (secondaryPaneSize > 1) {
            dispatch(setToolbarItemActiveStatus({ name: FEATURE_TABLE_TOOL, isActive: true }))
        } else {
            dispatch(setToolbarItemActiveStatus({ name: FEATURE_TABLE_TOOL, isActive: false }))
        }
    }

    const SplitterComponent = useCallback((component: SplitterLayout | null) => {
        component?.setState({ secondaryPaneSize: showFeatureTable ? tablePaneSize : 0 })
    }, [showFeatureTable])

    const handleActiveLayerChange = (item: LayerListItem) => {
        dispatch(setActiveLayer(item))
    }

    const handleUpdateLayerList = (items: Array<LayerListItem>) => {
        dispatch(setLayerList(items))
    }

    const handleLayerListControl = (value: boolean) => {
        dispatch(setShowLayerList(value))
    }

    const handleToolbarControl = (value: boolean) => {
        dispatch(setShowToolbar(value))
    }

    const handleToolbarItem = (name: string, isActive: boolean) => {
        dispatch(setToolbarItemActiveStatus({ name, isActive }))
    }

    const handleTilebarControl = (value: boolean) => {
        dispatch(setShowTilebar(value))
    }

    const handleHighlightFeatureById = (id: string) => {
        dispatch(setSelectedFeatureId(id))
    }

    const handleSelectedDrawGeometryTool = (drawGeomTool: SingleSelectionToolbarItem | null) => {
        dispatch(setSelectedDrawGeometryTool(drawGeomTool))
    }

    const handleDrawGeometry = (drawGeom: Geometry | null) => {
        dispatch(setDrawGeometry(drawGeom))
    }

    const handleAttributeQueryParams = (attributeQueryParams: Array<AttributeQueryParam>) => {
        dispatch(setAttributeQueryParams(attributeQueryParams))
    }

    const handleExtentGeometry = (extentGeometry: string) => {
        dispatch(setExtentGeometry(extentGeometry))
    }

    const handleSelectedFilter = (selectedFilter: string | null) => {
        dispatch(setSelectedFilter(selectedFilter))
    }

    const layers = (
        <>
            {layerList.map((layer, index) => {
                return (
                    <TileWmsLayer
                        baseUrl={config.BASE_API_URL}
                        key={index}
                        visible={layer.isChecked}
                        wmsLayerName={layer.wmsLayerName}
                    />
                );
            })}
        </>
    )

    return (
        <>
            <SplitterLayout
                ref={SplitterComponent}
                vertical
                percentage={true}
                primaryMinSize={60}
                secondaryInitialSize={0}
                onSecondaryPaneSizeChange={handleSecondaryPaneSize}
            >
                <MapToolkit
                    baseInstance={baseInstance}
                    layerKey={config.LAYER_KEY as any}
                    map={map}
                    height={`${mapHeight}vh`}
                    layerList={layerList}
                    activeLayer={activeLayer}
                    handleUpdateLayerList={handleUpdateLayerList}
                    handleActiveLayerChange={handleActiveLayerChange}
                    showLayerList={showLayerList}
                    handleLayerListControl={handleLayerListControl}
                    showToolbar={showToolbar}
                    showTileBar={showTileBar}
                    handleToolbarControl={handleToolbarControl}
                    toolbarItems={toolbarItems}
                    handleToolbarItem={handleToolbarItem}
                    handleTilebarControl={handleTilebarControl}
                    highlightFeatureById={selectedFeatureId}
                    handleHighlightFeatureById={handleHighlightFeatureById}
                    selectedDrawGeometryTool={selectedDrawGeometryTool}
                    handleSelectedDrawGeometryTool={handleSelectedDrawGeometryTool}
                    drawSource={drawSource}
                    drawGeometry={drawGeometry}
                    handleDrawGeometry={handleDrawGeometry}
                    attributeQueryParams={attributeQueryParams}
                    handleAttributeQueryParams={handleAttributeQueryParams}
                    extentGeometry={extentGeometry}
                    handleExtentGeometry={handleExtentGeometry}
                    selectedFilter={selectedFilter}
                    handleSelectedFilter={handleSelectedFilter}
                    layers={layers}
                />
                <FeatureTable />
            </SplitterLayout>
        </>
    )
}

export default Map
